import { useGetCanvas } from "@/lib/hooks/slides/editor/useGetCanvas";
import { useGetLiveMobileView } from "@/lib/hooks/slides/editor/useGetLiveMobileView";
import { useSlideEditorShortcuts } from "@/lib/hooks/slides/useSlideEditorShortcuts";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { setLayerModalOpen, setSelectedId } from "@/store/slideEditorSlice";
import { isFreeformTemplate, resolveSlideTemplate } from "@ai-teach/types";
import { Button, IconButton } from "@ai-teach/ui-web";
import { useEffect, useRef } from "react";
import { MdGridOff, MdGridOn } from "react-icons/md";
import { SelectedElementToolbar } from "../element-toolbar/indexToolbar";
import { AudioAutoplayPopup } from "./AudioAutoplayPopup";
import { ElementActionButtons } from "./ElementActionButtons";
import { LayerControlModal } from "./LayerControlModal";
import { RightClickMenu } from "./RightClickMenu";
import { useSlideEditorContext } from "./SlideEditorContext";

export function SlideCanvas() {
  const { isLiveView, setIsLiveView, isGridVisible, setIsGridVisible } = useSlideEditorContext();

  const dispatch = useAppDispatch();

  const isLayerModalOpen = useAppSelector((state) => state.slideEditor.present.isLayerModalOpen);
  const slide = useAppSelector((state) => state.slideEditor.present.slide);
  const showGridButton = isFreeformTemplate(resolveSlideTemplate(slide));
  const canvasRef = useRef<HTMLDivElement>(null);

  // Add keyboard shortcuts
  useSlideEditorShortcuts();

  // Get the appropriate canvas based on slide type and knowledge area
  const canvas = useGetCanvas();
  const liveView = useGetLiveMobileView();

  const handleOutsideClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      dispatch(setSelectedId(null));
    }
  };

  // Close layer modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (isLayerModalOpen) {
        const target = e.target as HTMLElement;
        // Check if click is outside layer modal, layer button, and right-click menu
        if (
          !target.closest("[data-layer-modal]") &&
          !target.closest("[data-layer-button]") &&
          !target.closest("[data-right-click-menu]")
        ) {
          dispatch(setLayerModalOpen(false));
        }
      }
    };

    if (isLayerModalOpen) {
      document.addEventListener("click", handleClickOutside);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [isLayerModalOpen, dispatch]);

  const handleIsLiveView = () => {
    setIsLiveView(!isLiveView);
    dispatch(setSelectedId(null));
  };

  return (
    <div onClick={handleOutsideClick}>
      <SelectedElementToolbar />
      <div className="flex flex-col gap-3">
        <div className="flex gap-2">
          <Button className="w-full" onClick={handleIsLiveView}>
            {isLiveView ? "Edit Mode" : "Live View"}
          </Button>
          {showGridButton && (
            <IconButton className="h-full" onClick={() => setIsGridVisible((prev) => !prev)}>
              {isGridVisible ? <MdGridOff /> : <MdGridOn />}
            </IconButton>
          )}
        </div>
        {isLiveView ? <div ref={canvasRef}>{liveView}</div> : <div ref={canvasRef}>{canvas}</div>}
      </div>
      <ElementActionButtons />
      <AudioAutoplayPopup />
      {isLayerModalOpen && <LayerControlModal />}
      <RightClickMenu />
    </div>
  );
}
